/**
 * Outbox del juez: cola de escrituras en IndexedDB.
 *
 * Lo que de verdad rompe una app de scoring no es la escala, es el wifi
 * del box. El juez captura igual, la cola guarda, y cuando vuelve la
 * senal se drena sola.
 *
 * Detalle clave: la clave de la cola es la clave NATURAL del score
 * (workoutId + registrationId), la misma que el UNIQUE de Postgres. De
 * ahi salen dos propiedades gratis:
 *
 *   1. Si el juez corrige el mismo carril tres veces sin conexion, se
 *      guarda una sola entrada, no tres escrituras encoladas.
 *   2. Reintentar es seguro: el upsert cae sobre la misma fila. Un envio
 *      duplicado no puede crear un score fantasma.
 */

import type { ScoreStatus } from '@timecap/core';

const DB_NAME = 'timecap';
const DB_VERSION = 1;
const STORE = 'outbox';

export interface ScorePayload {
  event_id: string;
  workout_id: string;
  registration_id: string;
  status: ScoreStatus;
  time_seconds: number | null;
  reps: number | null;
  load_kg: number | null;
  distance_m: number | null;
  capped: boolean;
  tiebreak_seconds: number | null;
  notes: string | null;
  judge_id: string | null;
  /**
   * Donde se quedo: bloques completos y reps del que dejo a medias.
   *
   * De aqui deriva Postgres las `reps`, asi que se manda el avance y no
   * el resultado. Null cuando el juez teclea las reps a mano --un WOD sin
   * movimientos definidos-- y entonces manda `reps`.
   */
  segments_done: number | null;
  partial_reps: number | null;
}

export interface QueuedScore {
  /** `${workout_id}:${registration_id}` -- la clave natural. */
  key: string;
  payload: ScorePayload;
  queuedAt: number;
  attempts: number;
  /** Se llena solo cuando el servidor RECHAZA (no cuando falla la red). */
  rejectedReason?: string;
}

export function queueKey(workoutId: string, registrationId: string): string {
  return `${workoutId}:${registrationId}`;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

/** Encola (o reemplaza) el score de un carril. */
export async function enqueue(payload: ScorePayload): Promise<QueuedScore> {
  const key = queueKey(payload.workout_id, payload.registration_id);
  const entry: QueuedScore = { key, payload, queuedAt: Date.now(), attempts: 0 };
  await tx('readwrite', (store) => store.put(entry));
  return entry;
}

export async function listQueued(): Promise<QueuedScore[]> {
  const all = await tx<QueuedScore[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedScore[]>);
  return all.sort((a, b) => a.queuedAt - b.queuedAt);
}

export async function removeQueued(key: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(key) as unknown as IDBRequest<undefined>);
}

export async function markAttempt(key: string, rejectedReason?: string): Promise<void> {
  const existing = await tx<QueuedScore | undefined>(
    'readonly',
    (store) => store.get(key) as IDBRequest<QueuedScore | undefined>,
  );
  if (!existing) return;
  await tx('readwrite', (store) =>
    store.put({ ...existing, attempts: existing.attempts + 1, rejectedReason }),
  );
}

/**
 * Un score rechazado por el servidor (permisos, WOD bloqueado, validacion)
 * NO se reintenta en bucle ni se descarta en silencio: se queda visible
 * para que el juez decida. Perder un score sin avisar es peor que fallar.
 */
export function isRejected(entry: QueuedScore): boolean {
  return Boolean(entry.rejectedReason);
}
